/**
 * ProvisioningProgress — inline polling component for BullMQ provisioning
 * pipelines (org-provision, user-provision).
 *
 * Drives the §9.A Create-Org wizard's Step 4 and is the v1.1-deferred
 * standalone /orgs/provisioning page (Phase 10 in lexportal-implementation-plan).
 *
 * Polls `GET /api/tasks/:taskId` (canonical endpoint per Phase A.9 audit
 * commit `35f840d3`; frozen response shape) at a 1s cadence until the
 * task reaches a terminal state (`completed` / `failed`). Emits
 * `onTerminal({ ok, task })` exactly once when terminal so the parent
 * can chain — for example, the wizard chains org-READY into
 * `createOrgUser`, then mounts a second `<ProvisioningProgress>` for
 * the user-provision job.
 *
 * UI: a vertical step list with per-step status icons + the current
 * step's progress copy. No external chart deps.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import type { TaskRecord, TaskStep, TaskStatus } from "@/types/platform";

const POLL_INTERVAL_MS = 1_000;

export interface ProvisioningProgressProps {
  taskId: string;
  /**
   * Fired exactly once when the task transitions to a terminal state.
   * Parent should use this to chain (e.g., wizard fires user-provision
   * after org READY) — NOT to close the dialog (parent owns that).
   */
  onTerminal?: (event: { ok: boolean; task: TaskRecord }) => void;
  /** Optional title shown above the step list (default "Provisioning"). */
  title?: string;
}

function isTerminal(status: TaskStatus): boolean {
  return status === "completed" || status === "failed";
}

function StepIcon({ status }: { status: TaskStep["status"] }) {
  if (status === "completed") return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-red-500" />;
  return <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600" />;
}

export function ProvisioningProgress({ taskId, onTerminal, title = "Provisioning" }: ProvisioningProgressProps) {
  const api = usePlatformApi();
  const [task, setTask] = useState<TaskRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guard onTerminal so a parent that re-renders during the same task
  // run doesn't get the callback fired twice.
  const terminalFiredRef = useRef(false);

  const poll = useCallback(async () => {
    try {
      const res = await api.getTask(taskId);
      setTask(res.data);
      setError(null);
      if (!terminalFiredRef.current && isTerminal(res.data.status)) {
        terminalFiredRef.current = true;
        onTerminal?.({ ok: res.data.status === "completed", task: res.data });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to read task status");
    }
  }, [api, taskId, onTerminal]);

  useEffect(() => {
    // Reset terminal-fired guard when taskId changes (parent supplies a
    // new id for the second pipeline run, e.g. user-provision after
    // org-provision completes).
    terminalFiredRef.current = false;
    setTask(null);
    setError(null);
    void poll();
    if (taskId) {
      const id = setInterval(() => {
        // Stop polling once we're terminal — keeps the network quiet.
        if (terminalFiredRef.current) {
          clearInterval(id);
          return;
        }
        void poll();
      }, POLL_INTERVAL_MS);
      return () => clearInterval(id);
    }
  }, [taskId, poll]);

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-3 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
        {error}
      </div>
    );
  }

  if (!task) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Starting {title.toLowerCase()}…
      </div>
    );
  }

  const statusLabel =
    task.status === "completed" ? "Completed" :
    task.status === "failed" ? "Failed" :
    task.status === "rolling_back" ? "Rolling back" :
    task.status === "running" ? "Running" : "Pending";

  return (
    <div className="space-y-2" data-testid={`provisioning-progress-${taskId}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-slate-600 dark:text-slate-300">{title}</span>
        <span
          className={
            task.status === "completed" ? "text-green-600 dark:text-green-400" :
            task.status === "failed" ? "text-red-600 dark:text-red-400" :
            "text-slate-500"
          }
          data-testid="provisioning-status"
        >
          {statusLabel} · {task.completedSteps}/{task.totalSteps}
        </span>
      </div>
      <ol className="space-y-1.5">
        {task.steps.map((step) => (
          <li
            key={step.step}
            className="flex items-center gap-2 text-xs"
            data-testid={`step-${step.name ?? step.step}`}
          >
            <StepIcon status={step.status} />
            <span className={
              step.status === "completed" ? "text-slate-600 dark:text-slate-300" :
              step.status === "running" ? "font-medium text-indigo-600 dark:text-indigo-400" :
              step.status === "failed" ? "font-medium text-red-600 dark:text-red-400" :
              "text-slate-400"
            }>
              {/* `name` is typed `string` but arrives ABSENT on user-provision
                  records written before lexserver 419152742 (an 8-name list
                  against a 10-step pipeline; write-time fix, no backfill). Show
                  the step NUMBER rather than an empty span, so a nameless step
                  is still legible as a row. */}
              {step.name ?? `Step ${step.step}`}
            </span>
          </li>
        ))}
      </ol>
      {task.status === "failed" && task.error && (
        <div className="rounded bg-red-50 p-2 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {typeof task.error === "string" ? task.error : JSON.stringify(task.error)}
        </div>
      )}
    </div>
  );
}
